import * as React from "react";
import { Link } from "react-router-dom";
import { APP_NAME } from "@/lib/branding";
import { cn } from "@/lib/utils";
import { Brand } from "../Branding/Brand";
import { Container } from "../Layout/Container";

export interface FooterLink {
  label: string;
  /** Internal route ("/build") or external URL ("https://…"). */
  href: string;
}

export interface FooterProps {
  links?: readonly FooterLink[];
  /** Replaces the default tagline. */
  children?: React.ReactNode;
  /** Show the Scryfall / Wizards of the Coast fan content notice. Default true. */
  legal?: boolean;
  className?: string;
}

const linkClass =
  "rounded-[var(--radius-base)] text-ds-gold-light/80 transition-colors hover:text-ds-gold-accent focus-visible:outline-none focus-visible:shadow-[var(--focus-ring)]";

/** Site footer: stacked brand, optional links, tagline and legal notice. */
export function Footer({ links, children, legal = true, className }: FooterProps) {
  return (
    <footer
      className={cn(
        "border-t border-ds-gold-primary/10 py-10 text-center font-body text-[length:var(--font-size-xs)] leading-[var(--line-height-relaxed)] text-ds-text-muted",
        className,
      )}
    >
      <Container className="flex flex-col items-center gap-4">
        <Brand layout="stacked" size="md" />
        {links && links.length > 0 && (
          <ul className="flex flex-wrap justify-center gap-x-6 gap-y-2 font-heading text-[length:var(--font-size-sm)] tracking-wide">
            {links.map((link) => (
              <li key={link.href}>
                {/^https?:\/\//.test(link.href) ? (
                  <a href={link.href} target="_blank" rel="noreferrer" className={linkClass}>
                    {link.label}
                  </a>
                ) : (
                  <Link to={link.href} className={linkClass}>
                    {link.label}
                  </Link>
                )}
              </li>
            ))}
          </ul>
        )}
        <p>{children ?? `${APP_NAME} · deterministic, explainable Commander deck building.`}</p>
        {legal && (
          <p className="max-w-2xl">
            Card images and data via{" "}
            <a href="https://scryfall.com" target="_blank" rel="noreferrer" className={linkClass}>
              Scryfall
            </a>
            . Magic: The Gathering is © Wizards of the Coast. This is unofficial Fan Content, not
            approved or endorsed by Wizards.
          </p>
        )}
      </Container>
    </footer>
  );
}
